import { useCallback, useEffect, useRef, useLayoutEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { readCatalogScrollPosition } from 'features/Catalogs/utils/catalogScrollPosition';
import {
  LAST_CLICKED_LISTING_SESSION_STORAGE_KEY,
  TAB_SWAP_SESSION_STORAGE_KEY,
} from 'consts';

const KOUTO_ROOT_ID = 'kouto-embed-root';
const ROOT_PATHNAMES = ['/', '/e', '/experiences'];

const SCROLL_TO_TOP_DELAY = 50; // Small delay to ensure route transition completes

const getScrollToValue = () => {
  const koutoRoot = document.getElementById(KOUTO_ROOT_ID);

  if (!koutoRoot) return 0;

  return koutoRoot.getBoundingClientRect().top + window.scrollY;
};

/** Is this route change a Browse↔Schedule tab swap? Peeked, not consumed — ScheduleViewTabs owns
 *  the anchor and clears it when it re-anchors the destination. */
const hasTabSwapAnchor = (): boolean => {
  try {
    return sessionStorage.getItem(TAB_SWAP_SESSION_STORAGE_KEY) != null;
  } catch {
    return false;
  }
};

const findCardByListingId = (listingId: string): HTMLElement | null => {
  const cards = document.querySelectorAll('[data-listing-id]');
  return Array.from(cards).find(
    (card) => (card as HTMLElement).dataset.listingId === listingId,
  ) as HTMLElement | null;
};

export const ScrollToTop = () => {
  const { pathname, search } = useLocation();
  const history = useHistory();
  const scrollTimeoutRef = useRef<number | null>(null);
  const cardScrollTimeoutRef = useRef<number | null>(null);
  const isTabSwapRef = useRef(false);

  const clearAllTimeouts = useCallback(() => {
    if (scrollTimeoutRef.current !== null) {
      clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = null;
    }
    if (cardScrollTimeoutRef.current !== null) {
      clearTimeout(cardScrollTimeoutRef.current);
      cardScrollTimeoutRef.current = null;
    }
  }, []);

  useLayoutEffect(() => {
    // Record whether this route change is a tab swap before ScheduleViewTabs consumes the anchor.
    // The read has to happen here rather than in the reset effect below: React flushes every layout
    // effect in a commit before any passive effect, so by then the anchor is gone. This component
    // is mounted above <App /> (src/index.js), so its layout effect runs before the destination
    // view's — keep it there if that tree is ever reshuffled.
    isTabSwapRef.current = hasTabSwapAnchor();

    const isRootPathname = ROOT_PATHNAMES.includes(pathname);

    if (!isRootPathname) {
      return () => {};
    }

    const lastClickedId = sessionStorage.getItem(
      LAST_CLICKED_LISTING_SESSION_STORAGE_KEY,
    );
    if (!lastClickedId) {
      return () => {};
    }

    const card = findCardByListingId(lastClickedId);

    if (card) {
      card.scrollIntoView({
        behavior: 'auto',
        block: 'center',
      });
      sessionStorage.removeItem(LAST_CLICKED_LISTING_SESSION_STORAGE_KEY);
    }

    return () => {};
  }, [pathname]);

  useEffect(() => {
    const isRootPathname = ROOT_PATHNAMES.includes(pathname);

    // On a Browse↔Schedule tab swap the destination view re-anchors the tab bar itself, so sit this
    // one out rather than making it out-wait us.
    // Back to a catalog page: the page restores the tile the guest opened, so do not pull it to
    // the top.
    const isCatalogRestore =
      history.action === 'POP' &&
      readCatalogScrollPosition(`${pathname}${search}`) !== null;

    if (isTabSwapRef.current || isRootPathname || isCatalogRestore) {
      return () => {};
    }

    if (scrollTimeoutRef.current !== null) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = window.setTimeout(() => {
      const scrollTopY = getScrollToValue();
      window.scrollTo({
        top: scrollTopY,
        left: 0,
        behavior: 'auto',
      });

      scrollTimeoutRef.current = null;
    }, SCROLL_TO_TOP_DELAY);

    return () => {
      if (scrollTimeoutRef.current !== null) {
        clearTimeout(scrollTimeoutRef.current);
        scrollTimeoutRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- scroll-to-top is per route change, not per search or action
  }, [pathname]);

  useEffect(() => {
    return clearAllTimeouts;
  }, [clearAllTimeouts]);

  return null;
};
